/* eslint-disable @typescript-eslint/no-explicit-any */
import { DocumentType, index, modelOptions, mongoose, PropType, post, pre, prop, Severity } from "@typegoose/typegoose";
import { z } from "zod";
import { type SproutInternalStateTracker } from "../../DeviceTypes/Device.js";
import { ObjectIdString } from "../../validators/index.js";
import { getZodSchemaFromClass, validate } from "../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { SproutLoggerFieldsWithDefaults, SproutLoggerSchemaTypes } from "../types/sproutLogger.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";
import { LoggerDownlinkResponse } from "./subSchemas/loggerDownlinkResponse.js";
import { LoggerFlags } from "./subSchemas/loggerFlags.js";
import { LoggerParsedDownlink } from "./subSchemas/loggerParsedDownlink.js";
import { LoggerParsedUplink } from "./subSchemas/loggerParsedUplink.js";
import { LoggerPayload } from "./subSchemas/loggerPayload.js";
import { LoggerTimes } from "./subSchemas/loggerTimes.js";

const SCHEMA_KEY = "SproutLogger" as const;

@index({ deviceID: 1, date: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "sproutloggers",
        timestamps: true,
        _id: true,
    },
})
@post<SchemaBase<SproutLogger>>(...addSchemaKeyHook(SCHEMA_KEY))
@pre<SproutLogger>("save", async function saveLogger(this: DocumentType<SproutLogger>) {
    if (this && this.errorLogs && this.errorLogs.length > 0) {
        this.hasErrors = true;
    }
    if (this.times) {
        // times often aren't correctly flagged as needing saving due to being a "mixed" type
        this.markModified("times");

        // insurance on making the damn things save
        try {
            this.times = { ...this.times };
        } catch (e) {
            // silently ignore
        }
    }
})
export class SproutLogger extends SchemaBase<SproutLogger, SproutLoggerFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.coerce.date(), responseOnly: true })
    @prop({ type: () => Date, required: true })
    public date!: Date;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public deviceID?: string;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public ownerID?: string;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public port?: number;

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: false })
    public downlinkLost?: boolean;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public uplinkRaw?: Record<string, any>;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public uplinkPayload?: Record<string, any> | LoggerPayload;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public uplinkPayloadParsed?: Record<string, any> | LoggerParsedUplink;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public downlinkPayload?: Record<string, any> | LoggerPayload;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public downlinkURL?: string;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({
        type: () => Object,
        required: false,
        allowMixed: Severity.ALLOW,
        default: () => new LoggerDownlinkResponse(),
    })
    public downlinkResponse?: Record<string, any> | LoggerDownlinkResponse = new LoggerDownlinkResponse();

    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public downlinkPayloadParsed?: Record<string, any> | LoggerParsedDownlink;

    @validate({
        childClass: LoggerTimes,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => LoggerTimes, required: false })
    public times?: LoggerTimes;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public ownerName?: string;

    @validate({ zodValidator: z.any().optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, default: () => new LoggerFlags() })
    public flags: Record<string, boolean> | LoggerFlags = new LoggerFlags();

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public applicationID?: string;

    @validate({ zodValidator: z.array(z.string()).optional(), responseOnly: true })
    @prop({ type: () => String, required: false, default: [] }, PropType.ARRAY)
    public logs: string[] = [];

    @validate({ zodValidator: z.array(z.string()).optional(), responseOnly: true })
    @prop({ type: () => String, required: false, default: [] }, PropType.ARRAY)
    public errorLogs: string[] = [];

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: false, default: false })
    public hasErrors?: boolean = false;

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: false, default: false })
    public test?: boolean = false;

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: false })
    public downlinkConfirmedReceived?: boolean;

    /** @deprecated Gen3 (Sprout) only — not used in Gen4. */
    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: false })
    public downlinkDebounced?: boolean;

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: false })
    public downlinkRequired?: boolean;

    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false })
    public reasonDownlinkRequired?: string;

    @validate({
        zodValidator: z
            .record(
                z.coerce.number(),
                z.object({
                    priority: z.number(),
                    chanceToSend: z.number(),
                    reason: z.string().optional(),
                }),
            )
            .optional(),
        responseOnly: true,
    })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public downlinkDecisionTable?: Record<
        number,
        {
            priority: number;
            chanceToSend: number;
            reason?: string;
        }
    >;

    /** @deprecated Never set in any production code path. */
    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public currentDeviceState?: SproutInternalStateTracker;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public stateUpdate?: Partial<SproutInternalStateTracker>;

    @validate({ zodValidator: z.record(z.string(), z.any()).optional(), responseOnly: true })
    @prop({ type: () => Object, required: false, allowMixed: Severity.ALLOW })
    public uplinkStateUpdate?: Partial<SproutInternalStateTracker>;

    @validate({ zodValidator: z.boolean().optional(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public downlinkSent: boolean = false;

    /** @deprecated Gen3 (Sprout) only — not used in Gen4. */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public scheduleGoodUntil?: Date;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public rssi?: number;

    @validate({ zodValidator: z.array(z.string()).optional(), responseOnly: true })
    @prop({ type: () => String, required: false }, PropType.ARRAY)
    public activePatches?: string[];

    static getters = {} as const satisfies Record<string, (self: SproutLogger) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutLoggerSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SproutLogger, SproutLoggerFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutLogger.dtoValidators = getZodSchemaFromClass(
    SproutLogger,
) as unknown as DtoValidatorTypes<SproutLoggerSchemaTypes>;
